/**
 * Legacy blog URL redirect handler.
 *
 * Background: before 2026-09 the blog articles lived at
 * `oroszcoaching.hu/{slug}`. The canonical structure is now
 * `/tudastar/{slug}`. Externally-shared links (Instagram, Facebook, Google
 * search results) still point at the old paths.
 *
 * Behaviour:
 *   1. Mount this component at `/:legacySlug` in App.js, positioned AFTER
 *      all specific routes but BEFORE the `*` catch-all.
 *   2. It calls `GET /api/blog/legacy-check/{slug}` — if the slug matches
 *      a published blog post the server responds `200 {redirect_to}`.
 *   3. On match we `window.location.replace()` to the new URL. This is a
 *      full page load with the URL updated in place (not history.pushState),
 *      which modern Googlebot honors together with the rel=canonical link
 *      already emitted by `<BlogPostPage>` on the target page. As close to a
 *      server-side 301 as a static-hosted SPA can practically achieve.
 *   4. On 404 (unknown path, not a blog slug) we fall back to `<Navigate to="/" replace />`
 *      — the previous behaviour of the catch-all route.
 *
 * If a true HTTP 301 is needed for critical seed links, configure a rewrite
 * rule at the ingress / reverse proxy layer in the Emergent hosting UI.
 */
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paths that must NEVER be treated as legacy blog slugs. React Router
// already routes these to their own `<Route>`, but we keep a defensive
// deny-list in case a route is renamed or a URL is typoed with a hash.
const RESERVED_TOP_LEVEL = new Set([
  '', 'admin', 'fiokom', 'tudastar', 'gyik', 'aszf', 'adatvedelem',
  'adatkezelesi-tajekoztato', 'impresszum', 'elallasi-nyilatkozat',
  'ingyenes-pdf', 'checkout', 'onboarding', 'egyedi-ajanlat',
  'eletmodvalto', 'hipertrofia', 'kiscsoportos-edzes', 'premium-coaching',
  'szemelyi-edzo-miskolc', 'online-szemelyi-edzo', 'unsubscribe', 'leiratkozas',
]);

export default function LegacyBlogRedirect() {
  const { legacySlug } = useParams();
  const [status, setStatus] = useState('checking'); // 'checking' | 'not-a-post'

  useEffect(() => {
    if (!legacySlug || RESERVED_TOP_LEVEL.has(legacySlug)) {
      setStatus('not-a-post');
      return;
    }
    let cancelled = false;
    axios.get(`${API}/blog/legacy-check/${encodeURIComponent(legacySlug)}`)
      .then((r) => {
        if (cancelled) return;
        const target = r?.data?.redirect_to;
        if (target) {
          // Full URL replace — Google treats this as a permanent move when
          // paired with the rel=canonical link on the target BlogPostPage.
          window.location.replace(target);
        } else {
          setStatus('not-a-post');
        }
      })
      .catch(() => { if (!cancelled) setStatus('not-a-post'); });
    return () => { cancelled = true; };
  }, [legacySlug]);

  if (status === 'not-a-post') {
    return <Navigate to="/" replace />;
  }
  // Empty render during the sub-second lookup — a spinner here would flash
  // annoyingly for the 99% of hits that resolve immediately.
  return null;
}
